import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { StatusBadge, money, jobTime } from "@/components/status";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Loader2, X, Star, Calendar, Clock } from "lucide-react";
import { toast } from "sonner";

const COLUMNS = [
  { key: "todo", label: "Pending / Open", statuses: ["open", "pending"] },
  { key: "accepted", label: "Accepted", statuses: ["accepted"] },
  { key: "in_progress", label: "In Progress", statuses: ["in_progress"] },
  { key: "completed", label: "Completed", statuses: ["completed", "disputed"] },
  { key: "done", label: "Approved / Paid", statuses: ["approved", "paid"] },
  { key: "cancelled", label: "Cancelled", statuses: ["cancelled"] },
];

export default function JobsBoard() {
  const [jobs, setJobs] = useState([]);
  const [properties, setProperties] = useState([]);
  const [cleaners, setCleaners] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fees, setFees] = useState({ host_fee_pct: 8, cleaner_fee_pct: 8 });
  const navigate = useNavigate();

  const blank = {
    property_id: "", date: "", time: "10:00", payout_amount: "120",
    instructions: "", cleaner_id: "", post_to_marketplace: true, pricing_mode: "fixed",
    is_recurring: false, recurrence: "weekly", occurrences: 4,
  };
  const [form, setForm] = useState(blank);
  const [checklist, setChecklist] = useState(["Kitchen surfaces", "Bathrooms", "Change linens", "Vacuum & mop", "Trash out"]);
  const [newItem, setNewItem] = useState("");

  const load = useCallback(() => api.get("/jobs").then((r) => setJobs(r.data)), []);
  useEffect(() => {
    load();
    api.get("/properties").then((r) => setProperties(r.data));
    api.get("/settings").then((r) => setFees(r.data)).catch(() => {});
  }, [load]);

  useEffect(() => {
    api.get("/cleaners", { params: form.date ? { date: form.date } : {} }).then((r) => setCleaners(r.data));
  }, [form.date]);

  const set = (k) => (e) => {
    const value = e && e.target !== undefined ? e.target.value : e;
    setForm({ ...form, [k]: value });
  };

  const create = async (e) => {
    e.preventDefault();
    if (!form.property_id) { toast.error("Select a property"); return; }
    if (!form.date) { toast.error("Select a date"); return; }
    setSaving(true);
    try {
      const payload = {
        property_id: form.property_id, date: form.date, time: form.time,
        payout_amount: Number(form.payout_amount), instructions: form.instructions,
        checklist, is_recurring: form.is_recurring, recurrence: form.recurrence,
        occurrences: Number(form.occurrences),
        cleaner_id: form.post_to_marketplace ? null : form.cleaner_id || null,
        post_to_marketplace: form.post_to_marketplace, pricing_mode: form.pricing_mode,
      };
      await api.post("/jobs", payload);
      toast.success(form.is_recurring ? "Recurring jobs created" : "Job created");
      setOpen(false); setForm(blank);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="jobs-board">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-black text-3xl tracking-tight text-sapphire">Job Pipeline</h1>
          <p className="text-muted-foreground text-sm mt-1">Track every clean from request to payout.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-ocean hover:bg-ocean/90" data-testid="create-job-button"><Plus className="w-4 h-4 mr-1" /> Create Job</Button>
          </DialogTrigger>
          <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">Create Cleaning Job</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4" data-testid="job-form">
              <div>
                <Label className="uppercase text-xs tracking-[0.2em] mb-1.5 block">Property</Label>
                <Select value={form.property_id} onValueChange={(v) => setForm({ ...form, property_id: v })}>
                  <SelectTrigger data-testid="job-property"><SelectValue placeholder="Select property" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {properties.length === 0 && <p className="text-xs text-signal mt-1">Add a property first.</p>}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-1">
                  <Label className="uppercase text-xs tracking-[0.2em] mb-1.5 block">Date</Label>
                  <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="job-date" />
                </div>
                <div className="col-span-1">
                  <Label className="uppercase text-xs tracking-[0.2em] mb-1.5 block">Time</Label>
                  <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="job-time" />
                </div>
                <div className="col-span-1">
                  <Label className="uppercase text-xs tracking-[0.2em] mb-1.5 block">{form.pricing_mode === "bidding" ? "Budget $ (optional)" : "Price $"}</Label>
                  <Input type="number" min={0} value={form.payout_amount} onChange={(e) => setForm({ ...form, payout_amount: e.target.value })} data-testid="job-payout" />
                </div>
              </div>

              <div className="border border-border rounded-md p-3 space-y-2" data-testid="pricing-mode">
                <Label className="font-semibold">Pricing</Label>
                <div className="grid grid-cols-2 gap-2">
                  {[["fixed", "Fixed price", "First eligible cleaner to accept gets the job."],
                    ["bidding", "Accept offers", "Cleaners propose a price; you choose who to hire."]].map(([v, l, d]) => (
                    <button key={v} type="button" onClick={() => setForm({ ...form, pricing_mode: v, post_to_marketplace: v === "bidding" ? true : form.post_to_marketplace })}
                      data-testid={`pricing-${v}`} aria-pressed={form.pricing_mode === v}
                      className={`text-left rounded-md border p-2.5 transition-colors ${form.pricing_mode === v ? "border-sapphire bg-sapphire text-white" : "border-border bg-white hover:border-sapphire"}`}>
                      <div className="text-sm font-semibold">{l}</div>
                      <div className={`text-xs mt-0.5 ${form.pricing_mode === v ? "text-white/70" : "text-muted-foreground"}`}>{d}</div>
                    </button>
                  ))}
                </div>
              </div>

              {form.pricing_mode === "fixed" && (
              <div className="rounded-md bg-slate-50 border border-border p-3 text-xs text-muted-foreground flex justify-between" data-testid="job-fee-preview">
                <span>You'll be charged <span className="font-semibold text-sapphire">${(Number(form.payout_amount || 0) * (1 + fees.host_fee_pct / 100)).toFixed(2)}</span> (incl. {fees.host_fee_pct}% service fee)</span>
                <span>Cleaner earns <span className="font-semibold text-emerald-700">${(Number(form.payout_amount || 0) * (1 - fees.cleaner_fee_pct / 100)).toFixed(2)}</span></span>
              </div>
              )}
              {form.pricing_mode === "bidding" && (
              <div className="rounded-md bg-slate-50 border border-border p-3 text-xs text-muted-foreground" data-testid="job-bid-note">
                The accepted offer becomes the job price. You'll pay the offer + {fees.host_fee_pct}% service fee; the cleaner receives the offer − {fees.cleaner_fee_pct}%.
              </div>
              )}

              <div>
                <Label className="uppercase text-xs tracking-[0.2em] mb-1.5 block">Instructions</Label>
                <Textarea value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} placeholder="Special notes for the cleaner…" data-testid="job-instructions" />
              </div>

              <div>
                <Label className="uppercase text-xs tracking-[0.2em] mb-1.5 block">Checklist</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {checklist.map((c, i) => (
                    <span key={`${c}-${i}`} className="inline-flex items-center gap-1 bg-slate-100 text-sm px-2 py-1 rounded">
                      {c}
                      <button type="button" onClick={() => setChecklist(checklist.filter((_, x) => x !== i))}><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input value={newItem} onChange={(e) => setNewItem(e.target.value)} placeholder="Add task" data-testid="checklist-input"
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (newItem) { setChecklist([...checklist, newItem]); setNewItem(""); } } }} />
                  <Button type="button" variant="outline" onClick={() => { if (newItem) { setChecklist([...checklist, newItem]); setNewItem(""); } }} data-testid="checklist-add">Add</Button>
                </div>
              </div>

              <div className="border border-border rounded-md p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="font-semibold">Post to Marketplace</Label>
                    <p className="text-xs text-muted-foreground">Any verified cleaner can accept.</p>
                  </div>
                  <Switch checked={form.post_to_marketplace} disabled={form.pricing_mode === "bidding"} onCheckedChange={(v) => setForm({ ...form, post_to_marketplace: v })} data-testid="job-marketplace-toggle" />
                </div>
                {!form.post_to_marketplace && (
                  <div>
                    <Label className="uppercase text-xs tracking-[0.2em] mb-1.5 block">Assign Cleaner</Label>
                    <Select value={form.cleaner_id} onValueChange={(v) => setForm({ ...form, cleaner_id: v })}>
                      <SelectTrigger data-testid="job-cleaner"><SelectValue placeholder="Select cleaner" /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {cleaners.map((c) => (
                          <SelectItem key={c.id} value={c.id} disabled={c.available_on_date === false}>
                            <span className="inline-flex items-center gap-2">
                              {form.date && (
                                <span className={`w-2 h-2 rounded-full ${c.available_on_date === false ? "bg-slate-300" : "bg-emerald-500"}`} />
                              )}
                              {c.name} {c.rating ? `· ★ ${c.rating}` : ""} {c.background_check_status !== "approved" ? "(unverified)" : ""}
                              {c.available_on_date === false && <span className="text-xs text-slate-400">Unavailable</span>}
                              {form.date && c.available_on_date !== false && <span className="text-xs text-emerald-600">Available</span>}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <div className="border border-border rounded-md p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="font-semibold">Recurring Job</Label>
                    <p className="text-xs text-muted-foreground">Auto-create repeat cleans.</p>
                  </div>
                  <Switch checked={form.is_recurring} onCheckedChange={(v) => setForm({ ...form, is_recurring: v })} data-testid="job-recurring-toggle" />
                </div>
                {form.is_recurring && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="uppercase text-xs tracking-[0.2em] mb-1.5 block">Frequency</Label>
                      <Select value={form.recurrence} onValueChange={(v) => setForm({ ...form, recurrence: v })}>
                        <SelectTrigger data-testid="job-recurrence"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="biweekly">Bi-weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="uppercase text-xs tracking-[0.2em] mb-1.5 block">Occurrences</Label>
                      <Input type="number" min={1} max={12} value={form.occurrences} onChange={(e) => setForm({ ...form, occurrences: e.target.value })} data-testid="job-occurrences" />
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button type="submit" disabled={saving} className="bg-ocean hover:bg-ocean/90" data-testid="job-submit">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Job"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex gap-4 overflow-x-auto pb-4" data-testid="kanban">
        {COLUMNS.map((col) => {
          const items = jobs.filter((j) => col.statuses.includes(j.status));
          return (
            <div key={col.key} className="w-80 flex-shrink-0" data-testid={`column-${col.key}`}>
              <div className="flex items-center justify-between px-1 mb-3">
                <h3 className="font-heading font-bold text-sm uppercase tracking-wide text-sapphire">{col.label}</h3>
                <span className="text-xs bg-slate-200 text-slate-600 rounded-full px-2 py-0.5 font-semibold">{items.length}</span>
              </div>
              <div className="space-y-3 min-h-[100px] bg-slate-50 rounded-md p-2 border border-border">
                {items.map((j) => (
                  <button
                    key={j.id}
                    onClick={() => navigate(`/jobs/${j.id}`)}
                    className="w-full text-left bg-white border border-border rounded-md p-3 hover:border-ocean hover:-translate-y-0.5 transition-all"
                    data-testid={`kanban-card-${j.id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-sapphire text-sm truncate">{j.property?.name}</span>
                      <StatusBadge status={j.status} />
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{j.date}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{jobTime(j)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <div className="w-6 h-6 rounded-full bg-ocean/10 text-ocean text-[10px] font-bold flex items-center justify-center">
                          {(j.cleaner?.name || "?").slice(0, 1).toUpperCase()}
                        </div>
                        <span className="text-xs text-slate-600">{j.cleaner?.name || "Unassigned"}</span>
                      </div>
                      <span className="font-heading font-bold text-sm text-sapphire">{money(j.payout_amount)}</span>
                    </div>
                  </button>
                ))}
                {items.length === 0 && <div className="text-center text-xs text-muted-foreground py-6">Empty</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
